/**
 * Parser for the hotel `extraInfo` blob that the booking engine (WebBeds) returns
 * on `/price/review` (`fareBreakdown.hotels[i].extraInfo`) and on the room-price
 * response (`rooms[i].roomPrices[j].extraInfo`).
 *
 * ── What the payload actually looks like ────────────────────────────────────
 * ONE string. HTML-ENTITY-ENCODED markup ("&lt;ul&gt;&lt;li&gt;…"), several
 * labelled sections packed together and separated by a comma that always FOLLOWS
 * a full stop, a closing paren or a closing tag:
 *
 *   "Rooms 1, 2: CheckIn instructions: &lt;ul&gt;&lt;li&gt;Extra-person charges…
 *    &lt;/li&gt;&lt;/ul&gt;,CheckIn special instructions: Front desk staff…
 *    tools.,Mandatory fees: &lt;p&gt;You&#039;ll be asked…:&lt;/p&gt;
 *    &lt;ul&gt;&lt;li&gt;Deposit: GBP 100 per person, per stay&lt;/li&gt;&lt;/ul&gt;,
 *    Optional fees: …,Know before you go: …,Tax Recovery Charges and Service
 *    Fees for 1 people: 37.76 GBP (This amount is included within the total price)."
 *
 * So there are three jobs, in this order:
 *   1. DECODE the entities once, which turns "&lt;li&gt;" into a real "<li>".
 *   2. SPLIT into sections on the ".," / ")," / ">," boundary — never on a bare
 *      comma, which would tear apart "GBP 1,234.56", "Rooms 1, 2:" and
 *      "per person, per stay".
 *   3. Turn each section's markup into PLAIN TEXT lines (`<li>` → a bullet,
 *      `</p>`/`<br>` → a line break, every other tag stripped).
 *
 * Step 3 is why this returns text lines rather than an HTML string: the content
 * is supplier free-text, and `dangerouslySetInnerHTML` on it would hand a third
 * party a script vector on the checkout screen. Nothing is lost — every tag
 * becomes structure (a bullet or a break) or is discarded as markup.
 *
 * An `extraInfo` that is missing, empty, or nothing but entity/whitespace filler
 * yields ZERO sections, so the call-site renders no "Extra Info" affordance at
 * all rather than a button that opens onto an empty dialog.
 */

/** One text line of a section. `bullet` marks text that came from an `<li>`. */
export interface ExtraInfoLine {
  text: string;
  bullet: boolean;
}

/** One labelled block, e.g. heading "Mandatory fees" + its lines. */
export interface ExtraInfoSection {
  /** "" when the engine sent the text with no label of its own. */
  heading: string;
  lines: ExtraInfoLine[];
}

/** A hotel's parsed extra info, kept with the hotel name for the dialog. */
export interface HotelExtraInfo {
  hotelName: string;
  sections: ExtraInfoSection[];
}

// Marks text lifted out of an `<li>` so the renderer can bullet it, and the
// section boundary. Both are control characters no supplier text can contain,
// and both are stripped again before anything is emitted.
const LI_MARK = "\u0001";
const SECTION_MARK = "\u0002";

/**
 * Decode HTML entities ONCE, via a detached <textarea>: assigning to `innerHTML`
 * and reading `value` decodes "&lt;", "&amp;", "&#039;", "&nbsp;" etc. without
 * ever attaching a node to the document, so nothing in the string can execute.
 * Same technique as the room-rate Extra Info accordion (RoomRateItem).
 */
const decodeHtmlEntities = (raw: string): string => {
  if (typeof document === "undefined") return raw;
  const el = document.createElement("textarea");
  el.innerHTML = raw;
  return el.value;
};

// A section break is a comma that CLOSES the previous section — one that follows
// a full stop, a closing paren, a closing tag, or an empty label's own colon:
//   "…translation tools.,Mandatory fees:"     → "."
//   "…within the total price).,Tax Recovery…" → ")"
//   "…&lt;/ul&gt;,Optional fees:"             → ">"
//   "…Additional fees for 2 people:,Name: …"  → ":"
// A bare comma is never a break, which is what keeps "GBP 1,234.56",
// "Rooms 1, 2:" and "per person, per stay" in one piece.
const SECTION_CLOSE_RE = /([.):>])\s*,\s*/g;

// …and a comma that OPENS the next section: one followed by a "Label:" of
// letters/digits/punctuation (no comma), which is how the engine introduces
// every section it sends. Needed because not every section ends in one of the
// four characters above ("…Cost: 2.06 USD,Name: resort_fee"). Written as a
// LOOKAHEAD so the label survives the split; deliberately not a lookBEHIND,
// which is ES2018 and still missing on older Safari (the same reason
// RoomRateItem's splitter is written replace-then-split).
const SECTION_OPEN_RE = /,\s*(?=[A-Z][A-Za-z0-9 ()/&'’.-]{0,60}:)/g;

// …and the third boundary, for the OTHER shape this field arrives in: a heading
// on a line of its OWN, the whole line being just a label and a colon ("Rate
// Notes:", "Hotel Tariff Notes:", "Moto Notes:") with its body on the lines
// below. Newline-delimited payloads have no commas to split on at all, so
// without this the entire multi-section blob would come through as one chunk.
const SECTION_LINE_RE = /\n(?=[^\n]{1,80}:[ \t]*(?:\n|$))/g;

/** Split the blob into sections on any of the three boundaries. */
const splitSections = (decoded: string): string[] =>
  decoded
    .replace(SECTION_CLOSE_RE, `$1${SECTION_MARK}`)
    .replace(SECTION_OPEN_RE, SECTION_MARK)
    .replace(SECTION_LINE_RE, SECTION_MARK)
    .split(SECTION_MARK)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

// A heading candidate must be short and must not read like a sentence — that is
// what keeps a colon INSIDE body prose ("the following charges at the property.
// Fees may include applicable taxes:") from being mistaken for a label.
const MAX_HEADING_LENGTH = 80;
const SENTENCE_BREAK_RE = /[.!?]\s/;

/**
 * Pull the leading "Label:" off a section, if it has one.
 *
 * Only the region BEFORE the first tag is considered, and the RIGHTMOST colon in
 * it wins — so "Rooms 1, 2: CheckIn instructions: <ul>…" keeps the whole
 * "Rooms 1, 2: CheckIn instructions" as the heading instead of splitting at the
 * first colon and leaving "CheckIn instructions:" stranded at the top of the body.
 *
 * A candidate must be a MULTI-WORD phrase, which is what every label the engine
 * actually sends looks like ("Mandatory fees", "Know before you go", "Tax
 * Recovery Charges and Service Fees for 2 people"). A single word before a colon
 * is part of the text, not a label ("Name: mandatory_tax", "Cost: 2.06 USD"), and
 * promoting one to a heading reads as a form field with a stray value under it.
 */
const splitHeading = (chunk: string): { heading: string; body: string } => {
  const firstTag = chunk.indexOf("<");
  const searchRegion = firstTag === -1 ? chunk : chunk.slice(0, firstTag);
  for (let at = searchRegion.lastIndexOf(":"); at > 0; at = searchRegion.lastIndexOf(":", at - 1)) {
    const candidate = searchRegion.slice(0, at).trim();
    if (!candidate || candidate.length > MAX_HEADING_LENGTH) continue;
    if (candidate.indexOf(" ") === -1) continue;
    if (SENTENCE_BREAK_RE.test(candidate)) continue;
    return { heading: candidate, body: chunk.slice(at + 1).trim() };
  }
  return { heading: "", body: chunk };
};

/**
 * Markup → text lines. `<li>` content becomes a bullet line; `<br>` and every
 * block close becomes a line break; anything else that looks like a tag is
 * dropped. Blank lines and exact duplicates within the section are removed —
 * the engine repeats lines freely and an identical line carries nothing new.
 */
const toLines = (body: string): ExtraInfoLine[] => {
  const flattened = body
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, `\n${LI_MARK}`)
    .replace(/<\s*\/\s*(li|p|ul|ol|div|tr|table|h[1-6])\s*>/gi, "\n")
    .replace(/<\s*(p|ul|ol|div|tr|td|table|h[1-6])[^>]*>/gi, "\n")
    .replace(/<[^>]*>/g, " ");
  const seen: { [line: string]: boolean } = {};
  const lines: ExtraInfoLine[] = [];
  flattened.split("\n").forEach((rawLine) => {
    const bullet = rawLine.indexOf(LI_MARK) !== -1;
    // Collapse the whitespace the markup left behind and drop the marker itself —
    // it was only ever a carrier for "this line came from an <li>".
    const text = rawLine.split(LI_MARK).join("").replace(/\s+/g, " ").trim();
    if (!text) return;
    if (seen[text]) return;
    seen[text] = true;
    lines.push({ text, bullet });
  });
  return lines;
};

export const parseMarkupLines = (raw: string | null | undefined): ExtraInfoLine[] => {
  if (!raw || typeof raw !== "string" || !raw.trim()) return [];
  return toLines(decodeHtmlEntities(raw));
};

/** Parse one `extraInfo` string into its sections (empty array when there is nothing to show). */
export const parseHotelExtraInfo = (raw: string | null | undefined): ExtraInfoSection[] => {
  if (!raw || typeof raw !== "string" || !raw.trim()) return [];
  const sections: ExtraInfoSection[] = [];
  const seen: { [key: string]: boolean } = {};
  splitSections(decodeHtmlEntities(raw)).forEach((chunk) => {
    const { heading, body } = splitHeading(chunk);
    const lines = toLines(body);
    // A section with neither a heading nor a body is filler. A heading with no
    // body IS kept: the engine uses one as a lead-in for the rows that follow
    // ("Additional fees for 2 people:"), so it carries meaning on its own.
    if (lines.length === 0 && !heading) return;
    const key = `${heading}|${lines.map((line) => line.text).join("|")}`;
    if (seen[key]) return;
    seen[key] = true;
    sections.push({ heading, lines });
  });
  return sections;
};

/**
 * Collect the extra info for every hotel in a `/price/review` `fareBreakdown.hotels`
 * array, dropping the hotels that have none. This is what the "Extra Info" button
 * is gated on: empty ⇒ no button.
 */
export const collectHotelExtraInfo = (hotels: any): HotelExtraInfo[] => {
  if (!Array.isArray(hotels)) return [];
  return hotels
    .map((hotel: any) => ({
      hotelName: typeof hotel?.hotelName === "string" ? hotel.hotelName : "",
      sections: parseHotelExtraInfo(hotel?.extraInfo),
    }))
    .filter((hotel) => hotel.sections.length > 0);
};
